import { useState, useEffect } from 'react'
import { Card, Space, Spin, Alert, Divider, Tag, Typography, Steps, Badge, theme } from 'antd'
import {
  CheckCircleOutlined, CalendarOutlined, GlobalOutlined,
  CloudOutlined, ShareAltOutlined, KeyOutlined,
} from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

const { Text, Title, Paragraph } = Typography

interface GoogleCalendarStatus {
  configured: boolean
  calendar_id?: string
  account_email?: string
  message?: string
}

export const GoogleCalendarSettings = () => {
  const { token } = theme.useToken()
  const [status, setStatus] = useState<GoogleCalendarStatus | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    axiosInstance.get<GoogleCalendarStatus>('/google/oauth/status')
      .then(r => setStatus(r.data))
      .catch(() => setStatus({ configured: false, message: 'Errore nel caricamento' }))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
  }

  return (
    <div style={{ maxWidth: 680 }}>

      {/* Stato connessione */}
      <Card
        title={<Space><CalendarOutlined style={{ color: token.colorPrimary }} /><span>Stato Connessione Google Calendar</span></Space>}
        style={{ marginBottom: 24 }}
      >
        {status?.configured ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="success"
              showIcon
              icon={<CheckCircleOutlined />}
              message="Google Calendar Attivo — Service Account"
              description="La connessione è permanente e non richiede rinnovo. Nessuna scadenza del token."
            />
            <div>
              <Text type="secondary">Account Service:</Text><br />
              <Tag color="green" style={{ marginTop: 4 }}>{status.account_email}</Tag>
            </div>
            <div>
              <Text type="secondary">Calendar ID:</Text><br />
              <Tag color="blue" style={{ marginTop: 4 }}>{status.calendar_id}</Tag>
            </div>
          </Space>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="Google Calendar non configurato"
            description={status?.message || 'Configura il Service Account seguendo la guida qui sotto.'}
          />
        )}
      </Card>

      {/* Guida setup */}
      <Card
        title={<Space><GlobalOutlined style={{ color: '#52c41a' }} /><span>Guida Configurazione Service Account</span></Space>}
        style={{ marginBottom: 24 }}
      >
        <Alert
          type="info"
          showIcon
          message="Perché Service Account?"
          description="A differenza del login OAuth utente (token scade ogni 7 giorni), il Service Account usa una chiave privata che non scade mai. Ideale per sistemi automatizzati come questo gestionale."
          style={{ marginBottom: 24 }}
        />

        <Steps
          direction="vertical"
          size="small"
          items={[
            {
              title: <Text strong>Apri Google Cloud Console</Text>,
              icon: <CloudOutlined />,
              description: (
                <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                  Vai su <Text code>console.cloud.google.com</Text> e seleziona il progetto
                  del tuo garage (o creane uno nuovo).
                </Paragraph>
              ),
            },
            {
              title: <Text strong>Abilita Google Calendar API</Text>,
              icon: <CalendarOutlined />,
              description: (
                <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                  Menu → <Text strong>API e servizi</Text> → <Text strong>Libreria</Text> →
                  cerca <Text code>Google Calendar API</Text> → clicca <Text strong>Abilita</Text>.
                </Paragraph>
              ),
            },
            {
              title: <Text strong>Crea il Service Account</Text>,
              icon: <KeyOutlined />,
              description: (
                <div style={{ marginTop: 8 }}>
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>Menu → <Text strong>IAM e amministrazione</Text> → <Text strong>Service Accounts</Text></li>
                    <li>Click <Text strong>+ Crea account di servizio</Text></li>
                    <li>Nome: <Text code>garage-calendar</Text> (o simile) → <Text strong>Crea e continua</Text></li>
                    <li>Ruolo: lascia vuoto → <Text strong>Continua</Text> → <Text strong>Fine</Text></li>
                  </ol>
                </div>
              ),
            },
            {
              title: <Text strong>Scarica la chiave JSON</Text>,
              icon: <KeyOutlined />,
              description: (
                <div style={{ marginTop: 8 }}>
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>Clicca sul Service Account appena creato</li>
                    <li>Tab <Text strong>Chiavi</Text> → <Text strong>Aggiungi chiave</Text> → <Text strong>Crea nuova chiave</Text></li>
                    <li>Seleziona <Text strong>JSON</Text> → <Text strong>Crea</Text></li>
                    <li>Salva il file scaricato — <Text type="danger">non puoi riscaricarlo</Text></li>
                  </ol>
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                    message="Il contenuto del JSON va inserito nella variabile GOOGLE_SERVICE_ACCOUNT_JSON nel file .env del backend, su una sola riga, tra apici singoli."
                  />
                </div>
              ),
            },
            {
              title: <Text strong>Condividi il calendario con il Service Account</Text>,
              icon: <ShareAltOutlined />,
              description: (
                <div style={{ marginTop: 8 }}>
                  <ol style={{ paddingLeft: 18, margin: 0 }}>
                    <li>Apri <Text code>calendar.google.com</Text></li>
                    <li>Trova il calendario → tre puntini → <Text strong>Impostazioni e condivisione</Text></li>
                    <li>Sezione <Text strong>Condividi con persone specifiche</Text> → <Text strong>+ Aggiungi</Text></li>
                    <li>
                      Incolla l'email del Service Account:<br />
                      <Text code style={{ fontSize: 12 }}>garage-calendar@tiber-garage.iam.gserviceaccount.com</Text>
                    </li>
                    <li>Permesso: <Text strong>Apportare modifiche agli eventi</Text></li>
                    <li>Click <Text strong>Invia</Text></li>
                  </ol>
                </div>
              ),
            },
            {
              title: <Text strong>Configura il Calendar ID nel .env</Text>,
              icon: <CalendarOutlined />,
              description: (
                <div style={{ marginTop: 8 }}>
                  <Paragraph style={{ margin: 0 }}>
                    Sempre nelle impostazioni del calendario → sezione <Text strong>Integrazione calendario</Text>:
                    copia il <Text strong>Calendar ID</Text> (es. <Text code>nome@gmail.com</Text> oppure
                    <Text code>abc123@group.calendar.google.com</Text>) e inseriscilo nel file <Text code>.env</Text>:
                  </Paragraph>
                  <Text code style={{ display: 'block', marginTop: 8, padding: '8px 12px', background: token.colorFillAlter, borderRadius: 4 }}>
                    GOOGLE_CALENDAR_ID="il-tuo-calendar-id"
                  </Text>
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Info tecniche */}
      <Card title={<Space><KeyOutlined /><span>Informazioni Tecniche</span></Space>} type="inner">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ background: token.colorFillAlter, padding: 12, borderRadius: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Tipo autenticazione</Text><br />
              <Text strong>Service Account (chiave privata)</Text>
            </div>
            <div style={{ background: token.colorFillAlter, padding: 12, borderRadius: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Scadenza token</Text><br />
              <Badge status="success" text={<Text strong>Nessuna</Text>} />
            </div>
            <div style={{ background: token.colorFillAlter, padding: 12, borderRadius: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Login utente richiesto</Text><br />
              <Text strong>No — accesso server-to-server</Text>
            </div>
            <div style={{ background: token.colorFillAlter, padding: 12, borderRadius: 6 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Sicurezza chiave</Text><br />
              <Text strong>File .env (non in git)</Text>
            </div>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <Alert
            type="warning"
            showIcon
            message="Non condividere mai il file JSON della chiave privata. Trattalo come una password."
          />
        </Space>
      </Card>
    </div>
  )
}

export default GoogleCalendarSettings
